import { useAuthQuery } from './useAuthQuery';
import { useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TimelineItem, ItemCategory, FunctionType } from '@/types/item';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { timeAsyncOperation } from '@/lib/performanceMonitor';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

interface ItemInput {
  project_id: string;
  name: string;
  category: ItemCategory;
  category_id?: string | null;
  start_date: string;
  end_date: string;
  item_type?: 'build' | 'service'; // ✅ NEW
  notes?: string;
  shop_resource_load?: number;
  specs_resource_load?: number;
  po_resource_load?: number;
  contract_resource_load?: number;
  manual_completion_percentage?: number | null;
  assigned_user_id?: string | null;
  depends_on?: { kind: 'item' | 'milestone'; id: string }[];
  planning_locked?: boolean;
  group_name?: string | null;
}

interface GateDataInput {
  item_id: string;
  function_type: FunctionType;
  gate_name: string;
  start_date?: string;
  end_date?: string;
  percentage?: number;
  has_warning?: boolean;
  is_complete?: boolean;
}

// ✅ Week 3: Type-safe interfaces for DB operations
interface DbItemGate {
  id?: string;
  item_id?: string;
  gate_name: string;
  percentage?: number | null;
  function_type: string;
  start_date?: string | null;
  end_date?: string | null;
  has_warning?: boolean;
  is_complete?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface DbItem {
  id: string;
  project_id: string | null;
  name: string;
  notes?: string | null;
  category: string;
  item_type?: string | null; // DB returns string, cast at transform
  start_date: string | null;
  end_date: string | null;
  sort_order: number;
  created_at?: string;
  updated_at?: string | null;
  created_by?: string;
  modified_by?: string | null;
  shop_resource_load?: number | null;
  specs_resource_load?: number | null;
  po_resource_load?: number | null;
  contract_resource_load?: number | null;
  manual_completion_percentage?: number | null;
  planning_locked?: boolean | null;
  group_name?: string | null;
  assigned_user_id?: string | null;
  project_name?: string;
  project_status?: string;
  status?: string;
  project?: { status?: string };
  gates?: DbItemGate[];
  timeline_data?: Record<string, unknown>;
}

interface RpcResult {
  success: boolean;
  error?: { code: string; message: string; type: string };
  data?: { items: DbItem[]; total_count: number };
  meta?: {
    pagination?: { limit: number; offset: number; has_more: boolean };
    filters_applied?: Record<string, unknown>;
    execution_time_ms?: number;
    has_filters?: boolean;
    timestamp?: number;
  };
}

interface ItemInsertData {
  name: string;
  category: string;
  project_id: string;
  start_date: string;
  end_date: string;
  notes?: string;
  shop_resource_load?: number;
  specs_resource_load?: number;
  po_resource_load?: number;
  contract_resource_load?: number;
  sort_order: number;
  group_name?: string | null;
  item_type?: 'build' | 'service';
  created_by?: string;
  modified_by?: string;
}

// Fallback function using original query method
const fallbackToOriginalQuery = async (projectId?: string, allowedProjectIds?: string[], searchTerm?: string, category?: string, itemType?: 'build' | 'service') => {
  return timeAsyncOperation(
    `items-query-fallback-${projectId || 'all'}`,
    'query',
    async () => {
      logger.debug('[Items Query] Using fallback method with original query');
      
      const { data, error } = await supabase.rpc('get_items_with_timeline_data', {
        project_id_param: projectId || null
      });

      if (error) throw error;

      let items = data || [];

      if (allowedProjectIds?.length) {
        items = items.filter((item) => allowedProjectIds.includes((item as DbItem).project_id as string));
      }
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        items = items.filter((item) => {
          const dbItem = item as DbItem;
          return dbItem.name?.toLowerCase().includes(searchLower) ||
            dbItem.notes?.toLowerCase().includes(searchLower);
        });
      }
      
      // Support both single category and array of categories
      if (Array.isArray(category) && category.length > 0) {
        items = items.filter((item) => category.includes((item as DbItem).category));
      } else if (category && typeof category === 'string') {
        items = items.filter((item) => (item as DbItem).category === category);
      }
      
      // ✅ Apply item_type filter client-side if specified (treat NULL as 'build')
      if (itemType === 'build') {
        items = items.filter((item) => (item as DbItem).item_type === 'build' || (item as DbItem).item_type == null);
      } else if (itemType === 'service') {
        items = items.filter((item) => (item as DbItem).item_type === 'service');
      }

      let transformedItems = (items as DbItem[]).map(transformItemData);

      // Hydrate projectStatus if missing
      const uniqueProjectIds = [...new Set(transformedItems.map(item => item.projectId).filter(Boolean))] as string[];
      if (uniqueProjectIds.length > 0) {
        try {
          const projectsData = await chunkedInQuerySimple<{ id: string; status: string }>(
            supabase,
            'projects',
            'id',
            uniqueProjectIds,
            'id, status'
          );

          if (projectsData) {
            const statusMap = new Map(projectsData.map(p => [p.id, p.status]));
            transformedItems.forEach(item => {
              if (!item.projectStatus && item.projectId && statusMap.has(item.projectId)) {
                item.projectStatus = statusMap.get(item.projectId)?.toLowerCase();
              }
            });
            logger.debug('[Items Hydration] Hydrated projectStatus for fallback items:', {
              totalItems: transformedItems.length,
              projectsCount: uniqueProjectIds.length
            });
          }
        } catch (e) {
          logger.error('[Items Hydration] Exception during project status hydration:', e);
        }
      }

      return transformedItems;
    },
    { projectId, hasSearch: !!searchTerm, hasCategory: !!category }
  );

};

// Transform database data to frontend format
const transformItemData = (dbItem: DbItem): TimelineItem => {
  const timelineData = {
    shop: { segments: {} } as any,
    specs: { segments: {} } as any,
    po: { segments: {} } as any,
    contract: { segments: {} } as any
  };

  // ✅ TRANSFORM 1: From optimized RPC "gates" array (Phase 7.7)
  if (dbItem.gates && Array.isArray(dbItem.gates)) {
    dbItem.gates.forEach((gate: DbItemGate) => {
      const funcType = gate.function_type as string;
      const gateName = gate.gate_name as string;
      
      if (funcType && gateName && timelineData[funcType]) {
        timelineData[funcType][gateName] = {
          start: gate.start_date,
          end: gate.end_date,
          percentage: gate.percentage || 0,
          hasWarning: gate.has_warning || false,
          isComplete: gate.is_complete || false
        };
      }
    });
  }

  // ✅ TRANSFORM 2: From fallback RPC "timeline_data" (merge - timeline_data takes priority)
  if (dbItem.timeline_data) {
    Object.keys(dbItem.timeline_data).forEach((functionType: string) => {
      const functionData = dbItem.timeline_data?.[functionType] as Record<string, unknown> | undefined;
      if (functionData && typeof functionData === 'object' && timelineData[functionType as FunctionType]) {
        timelineData[functionType as FunctionType] = {
          ...timelineData[functionType as FunctionType],
          ...(functionData as Record<string, unknown>)
        };
      }
    });
  }

  // Calculate resource status based on resource loads
  const totalLoad = (dbItem.shop_resource_load || 0) + 
                   (dbItem.specs_resource_load || 0) + 
                   (dbItem.po_resource_load || 0) + 
                   (dbItem.contract_resource_load || 0);
  
  // Map and validate project status with multiple fallbacks
  const allowedStatuses = ['intake', 'tender', 'build', 'defects', 'archive', 'service', 'inactive'];
  const rawStatus = (dbItem.project_status || dbItem.status || dbItem.project?.status || '').toLowerCase();
  const validatedStatus = allowedStatuses.includes(rawStatus) ? rawStatus : undefined;
  
  return {
    id: dbItem.id,
    name: dbItem.name,
    category: dbItem.category as ItemCategory,
    projectId: dbItem.project_id,
    projectName: dbItem.project_name || 'Unknown Project',
    projectStatus: validatedStatus,
    itemType: (dbItem.item_type as 'build' | 'service' | null) ?? 'build', // ✅ FIXED: Default NULL to 'build' for display
    startDate: dbItem.start_date,
    endDate: dbItem.end_date,
    timelineData,
    resourceStatus: totalLoad > 100 ? 'over' : 'ok',
    notes: dbItem.notes || '',
    lastModified: dbItem.updated_at,
    modifiedBy: dbItem.modified_by || 'System',
    shopResourceLoad: dbItem.shop_resource_load ?? 1,
    specsResourceLoad: dbItem.specs_resource_load ?? 1,
    poResourceLoad: dbItem.po_resource_load ?? 1,
    sortOrder: dbItem.sort_order,
    manual_completion_percentage: dbItem.manual_completion_percentage ?? null,
    planning_locked: dbItem.planning_locked ?? false,
    group_name: dbItem.group_name ?? null,
    assigned_user_id: dbItem.assigned_user_id ?? null
  };
};

// Build consistent query key for React Query
const buildItemsQueryKey = (
  projectId?: string, 
  allowedProjectIds?: string[], 
  searchTerm?: string, 
  category?: string,
  itemType?: 'build' | 'service' // ✅ NEW
): any[] => [
  'items-optimized',
  { projectId, allowedProjectIds, searchTerm, category, itemType }
];

// Sanitize search input
const sanitizeSearchTerm = (term?: string): string | undefined => {
  if (!term) return undefined;
  return term.trim().substring(0, 1000); // Limit length and trim whitespace
};

export const useItems = (
  projectId?: string, 
  allowedProjectIds?: string[], 
  searchTerm?: string, 
  category?: string,
  itemType?: 'build' | 'service' // ✅ NEW parameter
) => {
  const sanitizedSearchTerm = sanitizeSearchTerm(searchTerm);
  
  return useAuthQuery({
    queryKey: buildItemsQueryKey(projectId, allowedProjectIds, sanitizedSearchTerm, category, itemType),
    enabled: projectId !== undefined || allowedProjectIds !== undefined,
    queryFn: async () => {
      const startTime = performance.now();
      const isItemTypeEnabled = globalFeatureFlags.isEnabled('ENABLE_ITEM_TYPE_FILTERING');
      
      // Early return if no specific project and no allowed projects defined
      if (!projectId && (!allowedProjectIds || allowedProjectIds.length === 0)) {
        return [];
      }

      try {
        // First try the optimized RPC
        const { data: result, error } = await supabase.rpc('get_items_with_gates_optimized', {
          p_project_id: projectId || null,
          p_allowed_project_ids: allowedProjectIds || null,
          p_search_term: sanitizedSearchTerm || null,
          p_category_filter: category || null,
          p_item_type_filter: isItemTypeEnabled ? (itemType ?? null) : null, // ✅ undefined → null
          p_limit: 1000,
          p_offset: 0
        });

        if (error) {
          if (import.meta.env.DEV) {
            console.error('[Items Query] RPC error, falling back to original method:', error);
          }
          // Fallback to original function
          return await fallbackToOriginalQuery(projectId, allowedProjectIds, sanitizedSearchTerm, category, itemType);
        }

        // Type assertion for RPC result
        const rpcResult = result as unknown as RpcResult;
        
        if (!rpcResult?.success) {
          if (import.meta.env.DEV) {
            console.warn('[Items Query] RPC failed, falling back to original method:', rpcResult?.error);
          }
          return await fallbackToOriginalQuery(projectId, allowedProjectIds, sanitizedSearchTerm, category, itemType);
        }

        const duration = performance.now() - startTime;
        const items = rpcResult.data?.items || [];
        
        logger.info('[Items Query] Optimized fetch complete:', {
          itemCount: items.length,
          totalCount: rpcResult.data?.total_count,
          durationMs: Math.round(duration),
          executionTimeMs: rpcResult.meta?.execution_time_ms,
          hasFilters: rpcResult.meta?.has_filters
        });

        // Transform items using existing logic
        let transformedItems = items.map((item: DbItem) => transformItemData(item));

        // Hydrate manual_completion_percentage from DB to ensure accuracy
        const itemIds = transformedItems.map(i => i.id);
        if (itemIds.length > 0) {
          const manualRows = await chunkedInQuerySimple<{ id: string; manual_completion_percentage: number | null; planning_locked?: boolean | null; group_name?: string | null }>(
            supabase,
            'items' as any,
            'id',
            itemIds,
            'id, manual_completion_percentage, planning_locked, group_name'
          );
          
          if (manualRows && manualRows.length > 0) {
            const manualMap = new Map(manualRows.map((r) => [r.id, r.manual_completion_percentage]));
            const lockMap = new Map(manualRows.map((r) => [r.id, r.planning_locked]));
            const groupMap = new Map(manualRows.map((r) => [r.id, r.group_name]));
            transformedItems = transformedItems.map((i: TimelineItem) => ({
              ...i,
              manual_completion_percentage: manualMap.has(i.id) 
                ? (manualMap.get(i.id) ?? null) 
                : (i.manual_completion_percentage ?? null),
              planning_locked: lockMap.has(i.id)
                ? (lockMap.get(i.id) ?? false)
                : (i.planning_locked ?? false),
              group_name: groupMap.has(i.id)
                ? (groupMap.get(i.id) ?? null)
                : ((i as any).group_name ?? null),
            }));
            logger.debug('[Items Hydration] Hydrated manual_completion_percentage:', {
              totalItems: transformedItems.length,
              itemsWithManual: transformedItems.filter(i => i.manual_completion_percentage !== null).length
            });
          }
        }

        // Ensure sortOrder is present; hydrate from DB if missing
        const needsSortOrderHydration = transformedItems.some((i: TimelineItem) => i.sortOrder == null);
        if (needsSortOrderHydration && projectId) {
          const { data: orderRows, error: orderErr } = await supabase
            .from('items')
            .select('id, sort_order')
            .eq('project_id', projectId);

          if (!orderErr && orderRows) {
            const orderMap = new Map(orderRows.map((r) => [r.id, r.sort_order]));
            transformedItems = transformedItems.map((i: TimelineItem) => ({
              ...i,
              sortOrder: i.sortOrder ?? orderMap.get(i.id) ?? i.sortOrder
            }));
          }
        }

        // Hydrate projectStatus if missing
        const uniqueProjectIds = [...new Set(transformedItems.map(item => item.projectId).filter(Boolean))] as string[];
        if (uniqueProjectIds.length > 0) {
          try {
            const projectsData = await chunkedInQuerySimple<{ id: string; status: string }>(
              supabase,
              'projects' as any,
              'id',
              uniqueProjectIds,
              'id, status'
            );

            if (projectsData && projectsData.length > 0) {
              const statusMap = new Map(projectsData.map(p => [p.id, p.status]));
              transformedItems.forEach(item => {
                if (!item.projectStatus && item.projectId && statusMap.has(item.projectId)) {
                  item.projectStatus = statusMap.get(item.projectId)?.toLowerCase();
                }
              });
              logger.debug('[Items Hydration] Hydrated projectStatus:', {
                totalItems: transformedItems.length,
                itemsMissingStatus: transformedItems.filter(i => !i.projectStatus).length,
                projectsCount: uniqueProjectIds.length
              });
            }
          } catch (e) {
            logger.error('[Items Hydration] Exception during project status hydration:', e);
          }
        }
        
        return transformedItems;
      } catch (err) {
        const duration = performance.now() - startTime;
        if (import.meta.env.DEV) {
          console.error('[Items Query] Failed after', Math.round(duration), 'ms, falling back:', err);
        }
        return await fallbackToOriginalQuery(projectId, allowedProjectIds, sanitizedSearchTerm, category);
      }
    },
    requiresAuth: true,
    requiresSession: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: keepPreviousData, // ✅ Smooth UX when filters change
  });
};

export const useCreateItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (itemData: ItemInput) => {
      // ✅ FEATURE FLAG CHECK
      const isItemTypeEnabled = globalFeatureFlags.isEnabled('ENABLE_ITEM_TYPE_FILTERING');
      
      // ⚠️ CRITICAL: item_type is determined ONCE at creation time
      // 
      // Logic:
      // - If project.status = 'service' when item is created → item_type = 'service'
      // - Otherwise → item_type = 'build' (includes NULL project.status)
      // 
      // item_type NEVER changes after creation, even if project.status changes later
      // This field reflects the project PHASE when the item was originally created
      // 
      // Historical note: All items were reset to 'build' on 2025-11-14 due to
      // legacy data issues. Going forward, this field will be correctly set.
      let itemType: 'build' | 'service' = 'build'; // Default
      
      if (isItemTypeEnabled && !itemData.item_type) {
        // Only fetch project if item_type not explicitly provided
        const { data: project } = await supabase
          .from('projects')
          .select('status')
          .eq('id', itemData.project_id)
          .single();
        
        itemType = project?.status === 'service' ? 'service' : 'build';
        
        if (import.meta.env.DEV) {
          console.log('[ITEM_TYPE] Auto-set:', {
            projectId: itemData.project_id,
            projectStatus: project?.status,
            itemType
          });
        }
      }
      
      // Get the next sort_order for this project
      const { data: maxOrderData } = await supabase
        .from('items')
        .select('sort_order')
        .eq('project_id', itemData.project_id)
        .order('sort_order', { ascending: false })
        .limit(1);
      
      const nextSortOrder = (maxOrderData?.[0]?.sort_order || 0) + 1;
      
      const insertData: ItemInsertData = {
        name: itemData.name,
        category: itemData.category,
        project_id: itemData.project_id,
        start_date: itemData.start_date,
        end_date: itemData.end_date,
        notes: itemData.notes,
        shop_resource_load: itemData.shop_resource_load,
        specs_resource_load: itemData.specs_resource_load,
        po_resource_load: itemData.po_resource_load,
        contract_resource_load: itemData.contract_resource_load,
        sort_order: nextSortOrder,
        group_name: itemData.group_name ?? null,
        created_by: (await supabase.auth.getUser()).data.user?.id,
        modified_by: (await supabase.auth.getUser()).data.user?.id
      };
      
      // ✅ Only include item_type if flag is enabled
      if (isItemTypeEnabled) {
        insertData.item_type = itemType;
      }
      
      const { data, error } = await supabase
        .from('items')
        .insert(insertData)
        .select()
        .single();
      
      if (error) throw error;

      // Auto-set G1 to 50% for all functions
      const functionTypes = ['shop', 'specs', 'po', 'contract'] as const;
      
      for (const functionType of functionTypes) {
        await supabase
          .from('item_gate_data')
          .upsert({
            item_id: data.id,
            function_type: functionType,
            gate_name: 'g1',
            percentage: 50,
            start_date: itemData.start_date,
            end_date: itemData.end_date
          }, { 
            onConflict: 'item_id,function_type,gate_name',
            ignoreDuplicates: false 
          });
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate all items-optimized queries regardless of filter parameters
      queryClient.invalidateQueries({ queryKey: ['items-optimized'] });
      // Also invalidate legacy items queries for compatibility
      queryClient.invalidateQueries({ queryKey: ['items'] });
      toast({
        title: "Success",
        description: "Item created successfully",
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to create item';
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ItemInput> & { id: string }) => {
      const { data, error } = await supabase
        .from('items')
        .update({
          ...updates,
          modified_by: (await supabase.auth.getUser()).data.user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: async (updatedItem) => {
      // Invalidate all items-optimized queries regardless of filter parameters
      queryClient.invalidateQueries({ queryKey: ['items-optimized'] });
      // Also invalidate legacy items queries for compatibility
      queryClient.invalidateQueries({ queryKey: ['items'] });
      // Invalidate item categories to ensure dropdown syncs
      queryClient.invalidateQueries({ queryKey: ['item-categories'] });
      // Invalidate project-specific items
      queryClient.invalidateQueries({ 
        queryKey: ['project-items', updatedItem.project_id] 
      });
      
      // Force immediate refetch to ensure UI updates
      await queryClient.refetchQueries({ 
        queryKey: ['items-optimized'] 
      });
      
      toast({
        title: "Success",
        description: "Item updated successfully",
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to update item';
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // Use soft delete RPC instead of hard delete
      const { data, error } = await supabase.rpc('soft_delete_item', {
        item_id_param: id,
        deleted_by_param: null // Will use auth.uid() in function
      });
      
      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; name?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete item');
      }
      
      return result;
    },
    onSuccess: (data) => {
      // Invalidate all items-optimized queries regardless of filter parameters
      queryClient.invalidateQueries({ queryKey: ['items-optimized'] });
      // Also invalidate legacy items queries for compatibility
      queryClient.invalidateQueries({ queryKey: ['items'] });
      // Invalidate trash queries
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      // Invalidate project detail so summary_milestones orphan cleanup is visible
      queryClient.invalidateQueries({ queryKey: ['project-detail'] });
      
      toast({
        title: "Đã chuyển vào Thùng rác",
        description: data.name 
          ? `"${data.name}" sẽ bị xóa vĩnh viễn sau 30 ngày. Có thể khôi phục trong Thùng rác.`
          : "Item sẽ bị xóa vĩnh viễn sau 30 ngày.",
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Lỗi khi xóa item';
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateGateData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (gateData: GateDataInput) => {
      const { data, error } = await supabase
        .from('item_gate_data')
        .upsert(gateData, { 
          onConflict: 'item_id,function_type,gate_name',
          ignoreDuplicates: false 
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all items-optimized queries regardless of filter parameters
      queryClient.invalidateQueries({ queryKey: ['items-optimized'] });
      // Also invalidate legacy items queries for compatibility
      queryClient.invalidateQueries({ queryKey: ['items'] });
      toast({
        title: "Success",
        description: "Gate data updated successfully",
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to update gate data';
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });
};